import { useLayoutEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import type { BrowseSection } from '@api/fetchers/goal/test-tab/types';
import CircularLoader from '@base/CircularLoader/CircularLoader';
import { Fonts, mediaQuery, Spacings } from '@styles/index';

import { getBrowseCountLabel } from '../testTab/browseSections';

const BROWSE_LAYOUT_MS = 320;

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const Section = styled.section<{ $isRail: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $isRail }) =>
    $isRail ? Spacings.SPACING_2B : Spacings.SPACING_4B};
  width: ${({ $isRail }) => ($isRail ? '196px' : '100%')};
  max-width: 100%;
  flex-shrink: 0;
  transition: width ${BROWSE_LAYOUT_MS}ms ease, gap ${BROWSE_LAYOUT_MS}ms ease;

  ${mediaQuery.uptoMobile} {
    width: 100%;
  }
`;

const SectionTitle = styled.h2<{ $isRail: boolean }>`
  margin: 0;
  font-size: 12px;
  line-height: 16px;
  font-weight: ${Fonts.SEMIBOLD};
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-text-secondary);

  ${mediaQuery.uptoMobile} {
    display: ${({ $isRail }) => ($isRail ? 'none' : 'block')};
  }
`;

const Grid = styled.div<{ $isRail: boolean }>`
  display: grid;
  grid-template-columns: ${({ $isRail }) =>
    $isRail ? '1fr' : 'repeat(3, minmax(0, 1fr))'};
  gap: ${({ $isRail }) =>
    $isRail ? Spacings.SPACING_2B : Spacings.SPACING_4B};
  width: 100%;
  transition: gap ${BROWSE_LAYOUT_MS}ms ease;

  ${mediaQuery.uptoMobile} {
    ${({ $isRail }) =>
      $isRail
        ? `
      display: flex;
      flex-direction: row;
      gap: ${Spacings.SPACING_2B};
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
      scrollbar-width: none;

      &::-webkit-scrollbar {
        display: none;
      }
    `
        : 'grid-template-columns: repeat(2, minmax(0, 1fr));'}
  }
`;

const Card = styled.button<{
  $isRail: boolean;
  $isActive: boolean;
  $isFlipping: boolean;
}>`
  display: flex;
  align-items: ${({ $isRail }) => ($isRail ? 'center' : 'flex-start')};
  justify-content: ${({ $isRail }) =>
    $isRail ? 'space-between' : 'flex-start'};
  flex-direction: ${({ $isRail }) => ($isRail ? 'row' : 'column')};
  gap: ${({ $isRail }) => ($isRail ? '0' : Spacings.SPACING_2B)};
  width: 100%;
  min-height: ${({ $isRail }) => ($isRail ? '0' : '148px')};
  padding: ${({ $isRail }) =>
    $isRail
      ? `${Spacings.SPACING_2B} ${Spacings.SPACING_3B}`
      : Spacings.SPACING_4B};
  border: 1px solid
    ${({ $isRail, $isActive }) =>
      $isRail && $isActive
        ? 'var(--color-accent-blue)'
        : 'var(--color-divider)'};
  border-radius: ${({ $isRail }) => ($isRail ? '8px' : '12px')};
  background: var(--color-base-1);
  text-align: left;
  cursor: pointer;
  overflow: hidden;
  transform-origin: top left;
  transition: ${({ $isFlipping }) =>
    $isFlipping
      ? 'none'
      : `padding ${BROWSE_LAYOUT_MS}ms ease, min-height ${BROWSE_LAYOUT_MS}ms ease, border-radius ${BROWSE_LAYOUT_MS}ms ease, border-color 0.2s ease, transform 0.15s ease`};

  ${mediaQuery.uptoMobile} {
    ${({ $isRail }) => ($isRail ? 'min-width: 136px;' : 'min-height: 0;')}
  }

  ${({ $isRail, $isFlipping }) =>
    !$isRail && !$isFlipping
      ? `
    ${mediaQuery.aboveMobile} {
      &:hover {
        transform: scale(1.03);
      }
    }
  `
      : ''}

  &:focus-visible {
    outline: 2px solid var(--color-accent-blue);
    outline-offset: 2px;
  }
`;

const Icon = styled.img<{ $isRail: boolean; $isFlipping: boolean }>`
  width: ${({ $isRail }) => ($isRail ? '0' : '40px')};
  height: ${({ $isRail }) => ($isRail ? '0' : '40px')};
  opacity: ${({ $isRail, $isFlipping }) => ($isRail || $isFlipping ? 0 : 1)};
  overflow: hidden;
  object-fit: contain;
  transition: width ${BROWSE_LAYOUT_MS}ms ease,
    height ${BROWSE_LAYOUT_MS}ms ease, opacity 0.12s ease;
`;

const IconFallback = styled.span<{
  $isRail: boolean;
  $isFlipping: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ $isRail }) => ($isRail ? '0' : '40px')};
  height: ${({ $isRail }) => ($isRail ? '0' : '40px')};
  opacity: ${({ $isRail, $isFlipping }) => ($isRail || $isFlipping ? 0 : 1)};
  overflow: hidden;
  border-radius: 8px;
  background: var(--color-base-light-0);
  color: var(--color-accent-blue);
  font-size: 16px;
  font-weight: ${Fonts.SEMIBOLD};
  transition: width ${BROWSE_LAYOUT_MS}ms ease,
    height ${BROWSE_LAYOUT_MS}ms ease, opacity 0.2s ease;
`;

const Copy = styled.div<{ $isRail: boolean; $isFlipping: boolean }>`
  display: flex;
  flex-direction: ${({ $isRail }) => ($isRail ? 'row' : 'column')};
  align-items: ${({ $isRail }) => ($isRail ? 'center' : 'flex-start')};
  justify-content: ${({ $isRail }) =>
    $isRail ? 'space-between' : 'flex-start'};
  gap: ${Spacings.SPACING_2B};
  width: 100%;
  min-width: 0;
  opacity: ${({ $isFlipping }) => ($isFlipping ? 0 : 1)};
  transition: opacity 0.12s ease;
`;

const Heading = styled.h3<{ $isRail: boolean }>`
  margin: 0;
  font-size: ${({ $isRail }) => ($isRail ? '14px' : '16px')};
  line-height: ${({ $isRail }) => ($isRail ? '20px' : '22px')};
  font-weight: ${({ $isRail }) => ($isRail ? Fonts.REGULAR : Fonts.SEMIBOLD)};
  color: var(--color-text-primary);
`;

const Description = styled.p<{ $isRail: boolean }>`
  margin: 0;
  display: ${({ $isRail }) => ($isRail ? 'none' : 'block')};
  font-size: 13px;
  line-height: 18px;
  font-weight: ${Fonts.REGULAR};
  color: var(--color-text-secondary);
`;

const Count = styled.span<{ $isRail: boolean }>`
  margin-top: ${({ $isRail }) => ($isRail ? '0' : 'auto')};
  flex-shrink: 0;
  font-size: 12px;
  line-height: 16px;
  font-weight: ${({ $isRail }) => ($isRail ? Fonts.REGULAR : Fonts.SEMIBOLD)};
  color: ${({ $isRail }) =>
    $isRail ? 'var(--color-text-secondary)' : 'var(--color-accent-blue)'};
`;

const LoaderWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  grid-column: 1 / -1;
  min-height: 160px;
`;

interface BrowseCardsProps {
  sections: BrowseSection[];
  isLoading?: boolean;
  isRail?: boolean;
  activeSectionId?: string | null;
  onSelect: (section: BrowseSection) => void;
}

const BrowseCards = ({
  sections,
  isLoading = false,
  isRail = false,
  activeSectionId = null,
  onSelect
}: BrowseCardsProps) => {
  const itemRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const previousRectsRef = useRef<Record<string, DOMRect>>({});
  const previousIsRailRef = useRef(isRail);
  const [isFlipping, setIsFlipping] = useState(false);

  useLayoutEffect(() => {
    const nextRects: Record<string, DOMRect> = {};

    sections.forEach((section) => {
      const node = itemRefs.current[section.id];

      if (node) {
        nextRects[section.id] = node.getBoundingClientRect();
      }
    });

    const layoutChanged = previousIsRailRef.current !== isRail;
    const firstRects = previousRectsRef.current;

    if (
      layoutChanged &&
      !prefersReducedMotion() &&
      Object.keys(firstRects).length > 0
    ) {
      setIsFlipping(true);

      sections.forEach((section) => {
        const node = itemRefs.current[section.id];
        const first = firstRects[section.id];
        const last = nextRects[section.id];

        if (!node || !first || !last) {
          return;
        }

        const deltaX = first.left - last.left;
        const deltaY = first.top - last.top;

        node.style.transition = 'none';
        node.style.transform = `translate(${deltaX}px, ${deltaY}px)`;
        node.getBoundingClientRect();
        node.style.transition = `transform ${BROWSE_LAYOUT_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`;
        node.style.transform = '';
      });

      window.setTimeout(() => {
        sections.forEach((section) => {
          const node = itemRefs.current[section.id];

          if (node) {
            node.style.transition = '';
            node.style.transform = '';
          }
        });
        setIsFlipping(false);
      }, BROWSE_LAYOUT_MS);
    }

    previousRectsRef.current = nextRects;
    previousIsRailRef.current = isRail;
  }, [isRail, sections]);

  return (
    <Section aria-label="Browse tests" $isRail={isRail}>
      <SectionTitle $isRail={isRail}>Browse</SectionTitle>
      <Grid $isRail={isRail}>
        {isLoading && sections.length === 0 ? (
          <LoaderWrap>
            <CircularLoader color="var(--color-i-green)" />
          </LoaderWrap>
        ) : (
          sections.map((section) => {
            const countLabel = isRail
              ? String(section.totalTestCount)
              : getBrowseCountLabel(section);
            const isActive = section.id === activeSectionId;

            return (
              <Card
                key={section.id}
                ref={(node) => {
                  itemRefs.current[section.id] = node;
                }}
                type="button"
                $isRail={isRail}
                $isActive={isActive}
                $isFlipping={isFlipping}
                aria-current={isActive ? 'true' : undefined}
                onClick={() => onSelect(section)}
              >
                {section.iconUrl ? (
                  <Icon
                    src={section.iconUrl}
                    alt=""
                    $isRail={isRail}
                    $isFlipping={isFlipping}
                  />
                ) : (
                  <IconFallback
                    aria-hidden
                    $isRail={isRail}
                    $isFlipping={isFlipping}
                  >
                    {(section.heading || section.id).charAt(0)}
                  </IconFallback>
                )}
                <Copy $isRail={isRail} $isFlipping={isFlipping}>
                  <Heading $isRail={isRail}>{section.heading}</Heading>
                  {section.description ? (
                    <Description $isRail={isRail}>
                      {section.description}
                    </Description>
                  ) : null}
                  {countLabel ? (
                    <Count $isRail={isRail}>{countLabel}</Count>
                  ) : null}
                </Copy>
              </Card>
            );
          })
        )}
      </Grid>
    </Section>
  );
};

export default BrowseCards;
